import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Checkbox,
  Text,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Divider,
  Collapse,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';

const SearchAndFilters = forwardRef(
  (
    {
      searchQuery,
      setSearchQuery,
      hideOwnScambi,
      setHideOwnScambi,
      showOnlyOwnScambi,
      setShowOnlyOwnScambi,
      showOnlyWithInterested,
      setShowOnlyWithInterested,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    // Espone la funzione per aprire i filtri
    useImperativeHandle(ref, () => ({
      openFilters: () => setIsOpen(true),
    }));
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardShadow = useColorModeValue('lg', 'xl');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    const handleCheckboxChange = (setter, value, otherSetters) => {
      setter(value);
      // Disabilita gli altri checkbox
      otherSetters.forEach((setOther) => setOther(false));
    };

    return (
      <Box
        bg={cardBg}
        boxShadow={cardShadow}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        p={6}
        mb={6}
      >
        <VStack spacing={6} align="stretch">
          {/* Header cliccabile per aprire/chiudere l'accordion */}
          <Button
            variant="ghost"
            onClick={() => setIsOpen(!isOpen)}
            p={0}
            h="auto"
            _hover={{ bg: 'transparent' }}
          >
            <HStack spacing={3} justify="center">
              <Icon as={FaFilter} color="blue.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                Filtri e Ricerca
              </Text>
              <Icon
                as={isOpen ? FaChevronDown : FaChevronRight}
                color="blue.500"
                boxSize={4}
                transition="transform 0.2s"
              />
            </HStack>
          </Button>

          {/* Contenuto dell'accordion */}
          <Collapse in={isOpen} animateOpacity>
            <VStack spacing={6} align="stretch">
              {/* Search Bar - Centrata e prominente */}
              <Box textAlign="center">
                <HStack spacing={3} justify="center" maxW="500px" mx="auto">
                  <Icon as={FaSearch} color="gray.400" boxSize={5} />
                  <Input
                    placeholder="Cerca nome carta"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="lg"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                    }}
                    _hover={{ borderColor: 'gray.400' }}
                  />
                  {searchQuery && (
                    <Button
                      size="lg"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={() => setSearchQuery('')}
                      borderRadius="xl"
                      px={4}
                    >
                      Pulisci
                    </Button>
                  )}
                </HStack>
              </Box>

              <Divider />

              {/* Checkbox Filters - Layout responsive */}
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={3}
                  textAlign="center"
                >
                  Opzioni di Filtro
                </Text>
                <SimpleGrid
                  columns={{ base: 1, sm: 2, md: 3 }}
                  spacing={4}
                  justify="center"
                >
                  <Checkbox
                    isChecked={hideOwnScambi}
                    onChange={(e) =>
                      handleCheckboxChange(setHideOwnScambi, e.target.checked, [
                        setShowOnlyOwnScambi,
                        setShowOnlyWithInterested,
                      ])
                    }
                    colorScheme="blue"
                    size="md"
                    isDisabled={showOnlyOwnScambi || showOnlyWithInterested}
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontSize="md" color="gray.700" fontWeight="medium">
                        Nascondi i miei scambi
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Escludi tutti i tuoi scambi dalla lista
                      </Text>
                    </VStack>
                  </Checkbox>

                  <Checkbox
                    isChecked={showOnlyOwnScambi}
                    onChange={(e) =>
                      handleCheckboxChange(
                        setShowOnlyOwnScambi,
                        e.target.checked,
                        [setHideOwnScambi, setShowOnlyWithInterested]
                      )
                    }
                    colorScheme="green"
                    size="md"
                    isDisabled={hideOwnScambi || showOnlyWithInterested}
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontSize="md" color="gray.700" fontWeight="medium">
                        Mostra solo i miei scambi
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Visualizza solo i tuoi scambi
                      </Text>
                    </VStack>
                  </Checkbox>

                  <Checkbox
                    isChecked={showOnlyWithInterested}
                    onChange={(e) =>
                      handleCheckboxChange(
                        setShowOnlyWithInterested,
                        e.target.checked,
                        [setHideOwnScambi, setShowOnlyOwnScambi]
                      )
                    }
                    colorScheme="orange"
                    size="md"
                    isDisabled={hideOwnScambi || showOnlyOwnScambi}
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontSize="md" color="gray.700" fontWeight="medium">
                        Solo con soci interessati
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Scambi con contatti registrati
                      </Text>
                    </VStack>
                  </Checkbox>
                </SimpleGrid>
              </Box>
            </VStack>
          </Collapse>
        </VStack>
      </Box>
    );
  }
);

export default SearchAndFilters;
